import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import api, { getForumStats, getSimilarForumPosts } from '../../utils/api';
import { ArrowLeft, MessageSquare, ThumbsUp, ThumbsDown, Clock, Hash, Share2, Send, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';

// ── Utility: Build Comment Tree ───────────────────────────────────
const buildCommentTree = (flatComments) => {
    const map = {};
    const roots = [];

    flatComments.forEach(c => {
        map[c.id] = { ...c, children: [] };
    });

    flatComments.forEach(c => {
        if (c.parent_id && map[c.parent_id]) {
            map[c.parent_id].children.push(map[c.id]);
        } else {
            roots.push(map[c.id]);
        }
    });

    return roots;
};

// ── Sub-component: Comment Item (Extracted to fix focus bug) ──────
const CommentItem = ({
    comment,
    depth = 0,
    currentUser,
    activeReplyId,
    setActiveReplyId,
    replyContent,
    setReplyContent,
    isSubmittingReply,
    handleSubmitComment,
    handleVote,
    handleDeleteComment
}) => {
    const isReplying = activeReplyId === comment.id;

    return (
        <div className={`flex flex-col gap-3 py-4 ${depth > 0 ? 'ml-6 border-l border-slate-100 pl-4' : 'border-b border-slate-100'}`}>
            <div className="flex gap-3">
                {/* Comment Voting */}
                <div className="flex flex-col items-center gap-1 shrink-0">
                    <button onClick={() => handleVote('comment', comment.id, 1)} className="p-1 text-slate-300 hover:text-emerald-600 transition-colors"><ThumbsUp size={14} /></button>
                    <span className="font-bold text-slate-600 text-[11px]">{comment.upvotes - comment.downvotes}</span>
                    <button onClick={() => handleVote('comment', comment.id, -1)} className="p-1 text-slate-300 hover:text-red-500 transition-colors"><ThumbsDown size={14} /></button>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-slate-800">{comment.author_name}</span>
                        <span className="text-[10px] font-medium text-slate-400 flex items-center gap-1">
                            <Clock size={10} /> {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                    </div>
                    <div className="text-[13px] text-slate-600 leading-relaxed whitespace-pre-wrap">
                        {comment.content}
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                        {currentUser && (
                            <button
                                onClick={() => setActiveReplyId(isReplying ? null : comment.id)}
                                className="text-[10px] font-boldtracking-wider text-slate-400 hover:text-emerald-600 transition-colors flex items-center gap-1"
                            >
                                <MessageSquare size={10} /> {isReplying ? 'Cancel' : 'Reply'}
                            </button>
                        )}
                        {currentUser?.role === 'Admin' && (
                            <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-[10px] font-boldtracking-wider text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1"
                            >
                                <Trash2 size={10} /> Delete
                            </button>
                        )}
                    </div>

                    {isReplying && (
                        <div className="mt-3 bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
                            <textarea
                                value={replyContent[comment.id] || ''}
                                onChange={(e) => setReplyContent(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                placeholder="Write your reply..."
                                className="w-full h-20 p-3 bg-white border border-gray-200 rounded-lg focus:border-emerald-400 transition-all text-sm text-gray-900 resize-none outline-none mb-2"
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setActiveReplyId(null)}
                                    className="px-3 py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-700 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSubmitComment(null, comment.id)}
                                    disabled={isSubmittingReply[comment.id] || !(replyContent[comment.id] || '').trim()}
                                    className="px-4 py-1.5 bg-emerald-500 text-white text-[10px] font-boldtracking-wider rounded transition disabled:opacity-50"
                                >
                                    {isSubmittingReply[comment.id] ? 'Replying...' : 'Post Reply'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Render children recursively */}
            {comment.children && comment.children.length > 0 && (
                <div className="mt-1">
                    {comment.children.map(child => (
                        <CommentItem
                            key={child.id}
                            comment={child}
                            depth={depth + 1}
                            currentUser={currentUser}
                            activeReplyId={activeReplyId}
                            setActiveReplyId={setActiveReplyId}
                            replyContent={replyContent}
                            setReplyContent={setReplyContent}
                            isSubmittingReply={isSubmittingReply}
                            handleSubmitComment={handleSubmitComment}
                            handleVote={handleVote}
                            handleDeleteComment={handleDeleteComment}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const PostDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [post, setPost] = useState(null);
    const [sidebarPosts, setSidebarPosts] = useState([]);
    const [trendingTopics, setTrendingTopics] = useState([]);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarLoading, setSidebarLoading] = useState(true);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [activeReplyId, setActiveReplyId] = useState(null);
    const [replyContent, setReplyContent] = useState({});
    const [isSubmittingReply, setIsSubmittingReply] = useState({});

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await api.get('/auth/profile');
                setCurrentUser(res.data);
            } catch (err) {
                setCurrentUser(null);
            }
        };
        checkAuth();
    }, []);

    useEffect(() => {
        if (id) {
            fetchPost();
            fetchComments();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [id]);

    const fetchPostContext = async (postId) => {
        try {
            setSidebarLoading(true);
            const [similar, stats] = await Promise.all([
                getSimilarForumPosts(postId),
                getForumStats()
            ]);
            setSidebarPosts(similar || []);
            setTrendingTopics(stats.trending_topics || []);
        } catch (error) {
            console.error('Failed to load post context:', error);
        } finally {
            setSidebarLoading(false);
        }
    };

    const fetchPost = async () => {
        try {
            const res = await api.get(`/api/forum/posts/${id}`);
            setPost(res.data);
            fetchPostContext(id);
        } catch (error) {
            console.error('Failed to load post:', error);
            toast.error('Post not found.');
            navigate('/community');
        }
    };

    const fetchComments = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/forum/posts/${id}/comments`);
            setComments(res.data || []);
        } catch (error) {
            console.error('Failed to load comments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVote = async (itemType, itemId, voteValue) => {
        if (!currentUser) {
            toast.error('Please log in to vote.');
            navigate('/login');
            return;
        }

        try {
            await api.post('/api/forum/vote', {
                item_type: itemType,
                item_id: itemId,
                vote: voteValue
            });
            if (itemType === 'post') fetchPost();
            if (itemType === 'comment') fetchComments();
        } catch (error) {
            toast.error(error?.error || 'Failed to register vote.');
        }
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied to clipboard!");
    };

    const handleSubmitComment = async (e, parentId = null) => {
        if (e) e.preventDefault();
        const content = parentId ? replyContent[parentId] : newComment;

        if (!currentUser) {
            toast.error('Please log in to comment.');
            navigate('/login');
            return;
        }

        if (!content?.trim()) {
            toast.error("Comment cannot be empty.");
            return;
        }

        setIsSubmittingReply(prev => ({ ...prev, [parentId]: true }));
        if (!parentId) setSubmittingComment(true);

        try {
            await api.post(`/api/forum/posts/${id}/comments`, {
                content,
                parent_id: parentId
            });
            toast.success('Comment posted!');
            if (parentId) {
                setReplyContent(prev => ({ ...prev, [parentId]: '' }));
                setActiveReplyId(null);
            } else {
                setNewComment('');
            }
            fetchComments();
            fetchPost();
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || 'Failed to post comment.';
            toast.error(errorMsg);
        } finally {
            setIsSubmittingReply(prev => ({ ...prev, [parentId]: false }));
            if (!parentId) setSubmittingComment(false);
        }
    };

    const handleDeletePost = async () => {
        if (!window.confirm("Are you sure you want to delete this post?")) return;
        try {
            await api.delete(`/api/admin/forum/posts/${post.id}`);
            toast.success("Post deleted.");
            navigate('/community');
        } catch (error) {
            toast.error(error?.error || "Failed to delete post.");
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) return;
        try {
            await api.delete(`/api/admin/forum/comments/${commentId}`);
            toast.success("Comment deleted.");
            fetchComments();
            fetchPost();
        } catch (error) {
            toast.error(error?.error || "Failed to delete comment.");
        }
    };

    if (!post) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Icon name="Loader2" className="animate-spin text-emerald-600" size={32} />
            </div>
        );
    }

    const commentTree = buildCommentTree(comments);

    return (
        <div className="flex-1 flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <NavigationHeader />

            <div className="pt-16 pb-2 px-6 max-w-7xl mx-auto w-full">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Link to="/dashboard" className="hover:text-slate-900 transition-colors">
                        Dashboard
                    </Link>
                    <Icon name="ChevronRight" size={16} />
                    <Link to="/community" className="hover:text-slate-900 transition-colors">
                        Community
                    </Link>
                    <Icon name="ChevronRight" size={16} />
                    <span className="text-slate-900 font-medium line-clamp-1">{post.title}</span>
                </div>
            </div>

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-4 pb-20">

                <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-12 items-start">

                    {/* Left/Middle: Post Content */}
                    <div className="space-y-12">
                        <article className="space-y-10">
                            {/* Meta & Stats */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center border border-emerald-200 overflow-hidden">
                                        {post.author_avatar ? <img src={post.author_avatar} alt={post.author_name} className="w-full h-full object-cover" /> : <Icon name="User" size={24} className="text-emerald-600" />}
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-900 leading-none">{post.author_name}</h4>
                                        <p className="text-[11px] text-slate-400 font-medium mt-1.5 flex items-center gap-2">
                                            Posted {new Date(post.created_at).toLocaleDateString()}
                                            <span className="w-1 h-1 rounded-full bg-slate-200" />
                                            {post.views_count || 0} views
                                        </p>
                                    </div>
                                </div>
                                <span className="flex items-center gap-1.5 px-3 py-1 bg-[#F0FDF4] text-[#166534] text-[10px] font-bold rounded-full border border-[#DCFCE7]tracking-wider">
                                    <Icon name="CheckCircle2" size={12} className="text-emerald-600" />
                                    Verified Experience
                                </span>
                            </div>

                            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{post.title}</h1>

                            <div 
                                className="text-[15px] text-slate-600 leading-relaxed font-medium prose prose-emerald prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />

                            <div className="flex flex-wrap items-center gap-2 pb-10 border-b border-slate-100">
                                {post.tags?.map(t => (
                                    <span key={t} className="px-3 py-1 bg-[#F1F5F9] text-slate-600 text-[10px] font-bold rounded-md hover:bg-slate-200 transition-colors cursor-pointer">#{t}</span>
                                ))}
                            </div>

                            {/* Engagement Buttons */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-8">
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => handleVote('post', post.id, 1)} className="p-2 bg-[#F8FAFC] rounded-xl text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all active:scale-95">
                                            <Icon name="ThumbsUp" size={20} />
                                        </button>
                                        <span className="text-sm font-bold text-slate-900">{post.upvotes - post.downvotes}</span>
                                        <button onClick={() => handleVote('post', post.id, -1)} className="p-2 bg-[#F8FAFC] rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all active:scale-95">
                                            <Icon name="ThumbsDown" size={20} />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-400 font-bold text-xstracking-widest">
                                        <Icon name="MessageSquare" size={18} />
                                        <span>{comments.length} Comments</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={handleShare} className="p-2.5 bg-[#F8FAFC] rounded-xl text-slate-400 hover:text-emerald-600 transition-all"><Icon name="Share" size={18} /></button>
                                    <button className="p-2.5 bg-[#F8FAFC] rounded-xl text-slate-400 hover:text-emerald-600 transition-all"><Icon name="Bookmark" size={18} /></button>
                                    {currentUser?.role === 'Admin' && <button onClick={handleDeletePost} className="p-2.5 bg-red-50 rounded-xl text-red-400 hover:text-red-600 transition-all"><Icon name="Trash2" size={18} /></button>}
                                </div>
                            </div>
                        </article>

                        {/* Comments Section */}
                        <section className="pt-10 space-y-8">
                            {/* Comment Input */}
                            <div className="flex items-start gap-3 mb-10">
                                <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                    {currentUser?.full_name?.charAt(0) || <Icon name="User" size={18} />}
                                </span>
                                <div className="flex-1 relative">
                                    {!currentUser && (
                                        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl p-4 text-center">
                                            <p className="text-sm font-medium text-gray-600">
                                                Please <Link to="/login" className="text-emerald-600 font-bold hover:underline">login</Link> to join the discussion.
                                            </p>
                                        </div>
                                    )}
                                    <textarea
                                        value={newComment}
                                        onChange={e => setNewComment(e.target.value)}
                                        placeholder="Write your thoughts..."
                                        rows={4}
                                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 transition-all resize-none shadow-sm"
                                    />
                                    <div className="flex justify-end mt-4">
                                        <button
                                            onClick={(e) => handleSubmitComment(e)}
                                            disabled={submittingComment || !newComment.trim() || !currentUser}
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-boldtracking-[0.1em] px-6 py-2.5 rounded-lg transition-all shadow-md active:scale-95 disabled:opacity-40"
                                        >
                                            {submittingComment ? 'Posting...' : 'Post Comment'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6 pt-6">
                                {commentTree.map(c => (
                                    <CommentItem
                                        key={c.id}
                                        comment={c}
                                        currentUser={currentUser}
                                        activeReplyId={activeReplyId}
                                        setActiveReplyId={setActiveReplyId}
                                        replyContent={replyContent}
                                        setReplyContent={setReplyContent}
                                        isSubmittingReply={isSubmittingReply}
                                        handleSubmitComment={handleSubmitComment}
                                        handleVote={handleVote}
                                        handleDeleteComment={handleDeleteComment}
                                    />
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* Right: Sidebar */}
                    <aside className="hidden lg:block space-y-10 sticky top-24">
                        {/* Similar Discussions - Premium Light */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm relative overflow-hidden group">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-[10px] font-blacktracking-[0.2em] text-slate-400">Similar Discussions</h3>
                                <Icon name="MessageSquare" size={14} className="text-slate-300" />
                            </div>
                            <div className="space-y-6">
                                {sidebarPosts.length > 0 ? (
                                    sidebarPosts.map(p => (
                                        <Link key={p.id} to={`/community/post/${p.id}`} className="block group/item">
                                            <h4 className="text-sm font-bold text-slate-700 group-hover/item:text-emerald-600 transition-colors line-clamp-2 leading-snug mb-2">{p.title}</h4>
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-300tracking-wider">
                                                <span className="flex items-center gap-1"><Icon name="MessageSquare" size={10} /> {p.comment_count}</span>
                                                <span>•</span>
                                                <span>{new Date(p.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <p className="text-xs text-slate-400 italic font-medium">No similar discussions found</p>
                                )}
                            </div>
                        </div>

                        {/* Popular Tags - Premium Light */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm relative overflow-hidden group">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-[10px] font-blacktracking-[0.2em] text-slate-400">Popular Tags</h3>
                                <Icon name="Hash" size={14} className="text-slate-300" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {trendingTopics.length > 0 ? (
                                    trendingTopics.map((t, idx) => (
                                        <Link
                                            key={idx}
                                            to={`/community?tag=${t.tag}`}
                                            className="px-3.5 py-1.5 bg-slate-50 text-slate-500 text-[10px] font-bold rounded-lg border border-slate-100 hover:border-emerald-200 hover:text-emerald-600 transition-all cursor-pointer"
                                        >
                                            #{t.tag}
                                        </Link>
                                    ))
                                ) : (
                                    ['Interview', 'FAANG', 'Product'].map(t => (
                                        <span key={t} className="px-3.5 py-1.5 bg-slate-50 text-slate-400 text-[10px] font-bold rounded-lg border border-slate-100">#{t}</span>
                                    ))
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </main>
        </div>
    );
};

export default PostDetail;
